<template>
	<div class="p-component">
		<div v-if="fields.length > 0" class="p-fluid p-formgrid p-grid p-component p-input-filled" :class="maxFieldHeight ? 'fields-scroll' : ''" :style="{maxHeight: maxFieldHeight}">
			<div class="p-field p-input-filled p-col-12">
				<label :for="'input-fulltext'">{{$t('Squeeze.General.Fulltext')}}</label>
				<InputText v-model="fullText" id="input-fulltext" @keyup.enter="submit()"></InputText>
			</div>
			<div v-for="field in fields" :key="field.id" class="p-field p-input-filled p-col-12 p-md-6">
				<label :for="'input-' + field.id">{{field.name}}</label>
				<Calendar
					v-if="field.type === 'date'"
					id="range"
					style="margin-bottom: 0.15rem; border-radius: 4px;"
					v-model="selectedDate"
					@date-select="selectDate(field)"
					@change="selectDate(field)"
					selectionMode="range"
					:maxDate="maxDate"
					:showIcon="true"
				/>
				<InputText v-else v-model="field.filter.value" :id="'input-' + field.id" @keyup.enter="submit()"></InputText>
			</div>
		</div>
		<div class="p-fluid p-formgrid p-grid search-button-container">
			<div class="p-col-12">
				<Button @click="submit()" :disabled="!canSubmit">{{$t('Platform.Search.Title')}}</Button>
			</div>
		</div>
	</div>
</template>
<script lang="ts">
import {computed, defineComponent, PropType, ref} from 'vue';
import InputText from "primevue/inputtext";
import Calendar from 'primevue/calendar';
import {Filter} from "@/apps/squeeze/interfaces/DocumentSearch";
import {FilterMatchMode} from "primevue/api";
import {DocumentSearchRequestDto, FulltextFilterDto} from "@dex/squeeze-client-ts";
import {FieldValueFilterDto} from "@dex/squeeze-client-ts";
import moment from "moment";

/**
 * Defines a field that can be searched in
 */
interface SearchField {
	id: number;
	name: string;
	type: string;
}

/**
 * Defines a search mask that can be displayed
 */
export interface SearchMask {
	id: number;
	fields: Array<SearchField>;
}

type SearchRequest = DocumentSearchRequestDto;

export default defineComponent({
	name: 'DocumentSearchMask',
	props: {
		searchMask: {
			type: Object as PropType<SearchMask>,
			required: true,
		},
		maxFieldHeight: {
			type: String,
			required: false,
			default: "",
		},
	},
	components: {
		InputText,
		Calendar,
	},
	emits: [
		"submit",
	],

	setup(props, {emit}) {
		/** Fulltext string */
		const fullText = ref('');

		/** Max Date (current Date) */
		const maxDate = ref(new Date());

		/** Selected date */
		const selectedDate = ref<string[]>([]);

		const fields = computed(() => {
			return props.searchMask.fields.map(field => {
				const filter: Filter = {
					value: "",
					matchMode: FilterMatchMode.CONTAINS,
				}
				return Object.assign({}, field, {filter});
			});
		})

		const searchRequest = computed<SearchRequest>(() => {
			const fullTextArray: FulltextFilterDto[] = [];
			if (fullText.value) {
				fullTextArray.push({
					searchValue: "*" + fullText.value + "*",
					comp: 'raw',
				})
			}
			return {
				workflowContextFilters: [],
				fulltextFilters: fullTextArray,
				fieldFilters: fields.value.map(field => {
					const target: FieldValueFilterDto = {
						fieldId: field.id,
						searchValue: field.filter.value,
						comp: "raw" as any, // FIXME: Using the proper enum here somehow breaks our ts build.
						fieldType: field.type.toLowerCase(),
					}
					return target;
				}).filter(f => f.searchValue !== ""),
			}
		});

		const submit = () => {
			emit("submit", searchRequest.value);
		}

		const canSubmit = computed(() => {
			return fields.value.length > 0;
		});

		/**
		 * Triggered when Dates are selected
		 * @param field
		 */
		const selectDate = (field: any) => {
			if (selectedDate.value[1]) {
				field.filter.value = '[' + moment(selectedDate.value[0], 'LLLL').format('YYYY-MM-DD') + ' TO ' + moment(selectedDate.value[1], 'LLLL').format('YYYY-MM-DD') + ']';
			} else {
				field.filter.value = '[' + moment(selectedDate.value[0], 'LLLL').format('YYYY-MM-DD') + ' TO ' + moment().format('YYYY-MM-DD') + ']';
			}
		}

		return {
			fields,
			searchRequest,
			selectDate,
			submit,
			canSubmit,
			selectedDate,
			maxDate,
			fullText,
		}
	},
});

</script>
<style lang="scss" scoped>

.fields-scroll {
	overflow-y: auto;
	margin-bottom: 1rem;
}

.search-button-container {
	position: sticky;
	bottom: 0;
	background: var(--surface-overlay, var(--surface-a, #fff));
	padding-top: 0.5rem;
	z-index: 1;

	&::before {
		content: '';
		position: absolute;
		top: -0.5rem;
		left: 0;
		right: 0;
		height: 0.5rem;
		background: linear-gradient(to top, var(--surface-overlay, var(--surface-a, #fff)), transparent);
		pointer-events: none;
	}
}

/* width & height */
::-webkit-scrollbar {
	width: 0.525rem;
	height: 0.75rem;
}

/* Track */
::-webkit-scrollbar-track {
	background: #f1f1f1;
}

/* Handle */
::-webkit-scrollbar-thumb {
	background: var(--dex-primary-light-color);
	border-radius: 0.525rem;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
	background: var(--dex-primary-color);
}

::v-deep(.p-calendar-w-btn) {
	border: none !important;
}

</style>
